import * as React from 'react';
import Box from '@mui/material/Box';
import { DataGrid } from '@mui/x-data-grid';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import axios from 'axios';
import config from "../../config.js";
import EditIcon from '@mui/icons-material/Edit';
import {
  Autocomplete,
  TextField,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Modal,
  Button
} from '@mui/material';
import FileSaver from 'file-saver';
import CircularProgress from '@mui/material/CircularProgress';
import DownloadIcon from '@mui/icons-material/Download';

const API_URL = config['api_url'];
const Storage_URL = config['storage_base_url'];

export default function DataGridDemo({ data, types, templateMonth, templateYear,rolling_summary_url }) {

  const [localData, setLocalData] = React.useState(data);
  const [selectedType, setSelectedType] = React.useState('');
  const [selectedProgram, setSelectedProgram] = React.useState('');
  const [selectedChannel, setSelectedChannel] = React.useState('');
  const [selectedStatus, setSelectedStatus] = React.useState('');
  const [openEditModal, setOpenEditModal] = React.useState(false);
  const [editingRow, setEditingRow] = React.useState(null);
  const [selectedRowForStepper, setSelectedRowForStepper] = React.useState(null);
  const [downloadingCell, setDownloadingCell] = React.useState({ rowId: null, col: null });
  
  var pdfUrl = ''
  if(rolling_summary_url != ''){
     pdfUrl = rolling_summary_url
  }
  else{
    pdfUrl = ''
  }

  React.useEffect(() => {
    setLocalData(data);
  }, [data]);

  const excludedFields = ['_id', 'id', 'Site Name', 'templateId', 'status', 'types'];

  // Helper function to calculate delay between expected and completed dates
  const calculateDelay = (expectedDate, completedDate) => {
    if (!expectedDate || !completedDate) return null;
    
    const expected = new Date(expectedDate);
    const completed = new Date(completedDate);
    
    // Set time to 00:00:00 for accurate day comparison
    expected.setHours(0, 0, 0, 0);
    completed.setHours(0, 0, 0, 0);
    
    const diffInMs = completed - expected;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    return diffInDays;
  };

  // Helper function to check if plan date is delayed (should be from previous month)
  const isPlanDateDelayed = (planDate, templateMonth, templateYear) => {
    if (!planDate || templateMonth === undefined || templateMonth === null || !templateYear) return false;
    
    const planDateObj = new Date(planDate);
    const planMonth = planDateObj.getMonth(); // 0-indexed (0 = January, 1 = February, etc.)
    const planYear = planDateObj.getFullYear();
    
    // Calculate expected month (previous month)
    let expectedMonth, expectedYear;
    
    if (templateMonth === 0) { // January template
      expectedMonth = 11; // December
      expectedYear = templateYear - 1;
    } else {
      expectedMonth = templateMonth - 1;
      expectedYear = templateYear;
    }
    
    // Check if plan date is not in the expected previous month
    return !(planMonth === expectedMonth && planYear === expectedYear);
  };

  const typeOptions = [...new Set(localData.map(item => item['Type']))];
  const programNames = selectedType
    ? [...new Set(localData.filter(item => item['Type'] === selectedType).map(item => item['Program Name']))]
    : [];

  // Add channel options
  const channelOptions = [...new Set(localData.map(item => item['Channel']).filter(Boolean))];

  const statusOptions = [...new Set(localData.map(item => item['Status']).filter(Boolean))];

  const filteredData = localData.filter(row => {
    return (!selectedType || row['Type'] === selectedType) &&
      (!selectedProgram || row['Program Name'] === selectedProgram) &&
      (!selectedChannel || row['Channel'] === selectedChannel) &&
      (!selectedStatus || row['Status'] == selectedStatus);
  });

  const handleEditClick = (row) => {
    setEditingRow({ ...row });
    setOpenEditModal(true);
  };

  // Modified function to handle row click for stepper (no modal)
  const handleRowClick = (params) => {
    setSelectedRowForStepper(params.row);
  };

  const handleSaveEdit = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/admin/v1/disbursement/update/dashboardData`,
        editingRow
      );

      if (response.status === 200) {
        alert("Data Updated Successfully");
        console.log('Updated Row:', editingRow);

        setLocalData((prevData) =>
          prevData.map((row) =>
            row.id === editingRow.id ? editingRow : row
          )
        );

        setOpenEditModal(false);
      }
    } catch (error) {
      console.error('Error updating row:', error);
      alert('Failed to update');
    }
  };

  const handleDownload = async (row, colField) => {
    const sftp_path = row?.[`${colField}-Path`] ?? row?.[`${colField}-path`] ?? ""
    console.log(sftp_path, 'sftp_path')
    setDownloadingCell({ rowId: row.id, col: colField });

    try {
      const response = await axios.post(
        `${API_URL}/admin/v1/disbursement/downloadfile/sftp`,
        { path: sftp_path },
        { responseType: 'blob' }
      );

      if (response.status === 200) {
        const filename = sftp_path.split('/').pop() || 'downloaded_file';
        FileSaver.saveAs(response.data, filename);
      } else {
        alert('Download failed');
      }
    } catch (error) {
      console.error("Download error:", error);
      alert("Download failed");
    } finally {
      setDownloadingCell({ rowId: null, col: null });
    }
  };

  let columns = data.length > 0
    ? Object.keys(data[0]).map((key) => ({
      field: key,
      headerName:
        key === 'Count'
          ? 'Program Name'
          : key === 'types'
            ? 'Type'
            : key.charAt(0).toUpperCase() + key.slice(1),
      width: types === 'overall' || types === 'status' ? 250 : 150,
      renderCell: (params) => {
        if (key === 'Status') {
          const colorMap = {
            'Completed': 'green',
            'Working in progress': 'orange',
            'Pending for approval from P&G': 'darkgoldenrod'
          };
          return (
            <span style={{ color: colorMap[params.value] || 'default', fontWeight: 'bold' }}>
              {params.value}
            </span>
          );
        }
        
        // Special handling for Matrix Working Completed with delay highlighting
        if (key === 'Matrix Working Completed' && params.value) {
          const expectedDate = params.row['Matrix Working Expected completion Date'];
          let completedDate = params.value;
          
          // Extract date from "Yes On YYYY-MM-DD" format if needed
          if (typeof completedDate === 'string' && completedDate.includes('On ')) {
            const dateMatch = completedDate.match(/\d{4}-\d{2}-\d{2}/);
            if (dateMatch) {
              completedDate = dateMatch[0];
            }
          }
          
          if (expectedDate && completedDate) {
            const delayDays = calculateDelay(expectedDate, completedDate);
            
            if (delayDays > 0) {
              // Show red color and delay information for delayed completion
              return (
                <span style={{ color: 'red', fontWeight: 'bold' }}>
                  {params.value} - {delayDays} day{delayDays > 1 ? 's' : ''} delay
                </span>
              );
            } else {
              // Show green for on-time or early completion
              return (
                <span style={{ color: 'green', fontWeight: 'bold' }}>
                  {params.value}
                </span>
              );
            }
          }
        }
        
        return params.value;
      },
    }))
    : [];

  if (types === 'status') {
    columns = columns.filter(col =>
      ['Type', 'Program Name', 'Status', 'Channel'].includes(col.field)
    );
  } else {
    columns = columns.filter(col => !excludedFields.includes(col.field) && !col.field.toLowerCase().includes('path'));
  }

  const locationColumn = columns.find(col => col.field === 'Location');
  const grandTotalColumn = columns.find(col => col.field === 'Grand Total');

  columns = columns.filter(col => col.field !== 'Location' && col.field !== 'Grand Total');

  if (columns.length === 2 && columns[0] && columns[1]) {
    const countIndex = columns.findIndex(col => col.field === 'Count');
    if (countIndex === 0) {
      [columns[0], columns[1]] = [columns[1], columns[0]];
    }
  }

  if (locationColumn) columns.unshift(locationColumn);
  if (grandTotalColumn) columns.push(grandTotalColumn);

  let finalColumns = [];

  columns.forEach(col => {
    finalColumns.push(col);

    if (col.field !== 'Location' && col.field !== 'Grand Total' && types != 'status' && types != 'overall') {
      finalColumns.push({
        field: `downloadFile_${col.field}`,
        headerName: "Download File",
        width: 200,
        sortable: false,
        renderCell: (params) => {
          const isLoading = downloadingCell.rowId === params.row.id && downloadingCell.col === col.field;

          return isLoading ? (
            <CircularProgress size={24} />
          ) : (
            <FileDownloadIcon
              style={{ fontSize: "24px", cursor: "pointer", color: "#2185D0" }}
              onClick={(e) => {
                e.stopPropagation(); // Prevent row click
                handleDownload(params.row, col.field);
              }}
            />
          );
        },
      });
    }
  });

  if (types != 'overall') {
    finalColumns.unshift({
      field: 'edit',
      headerName: 'Edit',
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <EditIcon
          onClick={(e) => {
            e.stopPropagation(); // Prevent row click
            handleEditClick(params.row);
          }}
          sx={{
            color: '#2185D0',
            cursor: 'pointer',
            '&:hover': { color: '#145a86' }
          }}
        />
      ),
    });
  }

  const gridProps = {
    rows: filteredData,
    columns: finalColumns,
    onRowClick: types == 'status' ? handleRowClick : '', // Add row click handler
    sx: {
      '& .MuiDataGrid-columnHeaders': {
        backgroundColor: '#2185D0',
        color: 'white',
      },
      '& .MuiDataGrid-row': {
        cursor: 'pointer',
      },
    }
  };

  // Modified box styles to accommodate side-by-side layout
  const getBoxStyles = () => {
    if (types === 'overall') {
      return { height: 500, width: selectedRowForStepper ? '40%' : '40%' };
    } else if (types === 'status') {
      return { height: 600, width: selectedRowForStepper ? '50%' : '100%' };
    } else {
      return { height: 500, width: selectedRowForStepper ? '50%' : '100%' };
    }
  };

  // Stepper component
  const renderStepper = (row) => {
    const mainStepperFields = [
      'Input recevied',
      'Plan Recevied from P&G',
      'Matrix Working Expected completion Date',
      'Matrix Working Completed',
      'Shared for P&G  Approval',
      'P&G Approved',
      'Working/Credit note shared to site'
    ];

    const getCombinedValue = (row, mainField) => {
      const mainValue = row[mainField];
      if (mainValue === undefined || mainValue === null) return { text: null, dateValue: null, mainField };

      let displayText = String(mainValue);

      const dateMapping = {
        'Input recevied': null,
        'Plan Recevied from P&G': 'Plan Recevied Date from P&G',
        'Matrix Working Expected completion Date': null,
        'Matrix Working Completed': 'Matrix Working Completed Date',
        'Shared for P&G  Approval': 'Shared for P&G  Approval Date',
        'P&G Approved': 'P&G Approved Date',
        'Working/Credit note shared to site': 'Working/Credit note shared to site Date'
      };

      let dateValue = null;
      const dateFieldName = dateMapping[mainField];
      if (dateFieldName && row[dateFieldName]) {
        dateValue = row[dateFieldName];
      }

      const remarksMapping = {
        'Input recevied': 'Input Recevied Remarks',
        'Plan Recevied from P&G': 'Plan Received Remarks',
        'Matrix Working Expected completion Date': null,
        'Matrix Working Completed': 'Matrix Working Remarks',
        'Shared for P&G  Approval': 'Shared for Approval Remarks',
        'P&G Approved': 'P&G Approved Remarks',
        'Working/Credit note shared to site': 'Working/Credit note shared Remarks'
      };

      const remarksFieldName = remarksMapping[mainField];
      let remarksValue = null;
      if (remarksFieldName && row[remarksFieldName]) {
        remarksValue = row[remarksFieldName];
      }

      // Special handling for Matrix Working Completed with delay information
      if (mainField === 'Matrix Working Completed' && mainValue) {
        const expectedDate = row['Matrix Working Expected completion Date'];
        if (expectedDate) {
          let completedDate = mainValue;
          
          // Extract date from "Yes On YYYY-MM-DD" format if needed
          if (typeof completedDate === 'string' && completedDate.includes('On ')) {
            const dateMatch = completedDate.match(/\d{4}-\d{2}-\d{2}/);
            if (dateMatch) {
              completedDate = dateMatch[0];
            }
          }
          
          const delayDays = calculateDelay(expectedDate, completedDate);
          if (delayDays > 0) {
            displayText += ` - ${delayDays} day${delayDays > 1 ? 's' : ''} delay`;
          }
        }
      }

      if (dateValue) {
        displayText += ` On ${dateValue}`;
      }

      if (remarksValue) {
        displayText += ` - ${remarksValue}`;
      }

      return { text: displayText, dateValue, mainField, remarksValue };
    };

    let firstEmptyFound = false;

    return (
      <Box sx={{ mb: 4 }}>
        <Stepper orientation="vertical" nonLinear activeStep={-1}>
          {mainStepperFields.map((key) => {
            const combinedResult = getCombinedValue(row, key);
            const combinedValue = combinedResult?.text;
            const dateValue = combinedResult?.dateValue;
            const remarksValue = combinedResult?.remarksValue;
            let mainValue;

            if (key === 'Matrix Working Completed') {
              mainValue = row['Matrix Working Completed Date'];
            } else {
              mainValue = row[key];
            }

            let displayValue = combinedValue || 'Pending';
            let stepColor = 'black';

            if (mainValue !== undefined && mainValue !== null && mainValue !== '') {
              // Special color handling for Matrix Working Completed with delay
              if (key === 'Matrix Working Completed') {
                const expectedDate = row['Matrix Working Expected completion Date'];
                if (expectedDate) {
                  let completedDate = mainValue;
                  console.log(completedDate,'completedDate1')
                  // Extract date from "Yes On YYYY-MM-DD" format
                  if (typeof completedDate === 'string' && completedDate.includes('On ')) {
                    const dateMatch = completedDate.match(/\d{4}-\d{2}-\d{2}/);
                    console.log(dateMatch,'dateMatch')
                    if (dateMatch) {
                      completedDate = dateMatch[0];
                    }
                  }

                  console.log(expectedDate,'expectedDate')
                  console.log(completedDate,'completedDate')

                  const delayDays = calculateDelay(expectedDate, completedDate);
                  console.log(delayDays,'delaydays')
                  stepColor = delayDays > 0 ? 'red' : 'green';

                  if (delayDays > 0) {
                    displayValue = `${combinedValue} - ${delayDays} day${delayDays > 1 ? 's' : ''} delay`;
                  } else {
                    displayValue = combinedValue;
                  }
                } else {
                  stepColor = 'green';
                  displayValue = combinedValue;
                }
              } 
              // Special handling for Plan Received from P&G date validation
              else if (key === 'Plan Recevied from P&G' && dateValue) {
                stepColor = 'green';
                
                // Check if plan date is delayed (should be from previous month)
                const isPlanDelayed = isPlanDateDelayed(dateValue, templateMonth, templateYear);
                
                if (isPlanDelayed) {
                  // Create display value with red highlighted date
                  const baseText = mainValue;
                  const remarksText = remarksValue ? ` - ${remarksValue}` : '';
                  
                  displayValue = (
                    <span>
                      {baseText} On <span style={{ color: 'red', fontWeight: 'bold', backgroundColor: '#ffebee', padding: '2px 4px', borderRadius: '3px' }}>{dateValue} {remarksText}</span>
                      
                    </span>
                  );
                } else {
                  displayValue = combinedValue;
                }
              } else {
                stepColor = 'green';
                displayValue = combinedValue;
              }
            } else {
              if (!firstEmptyFound) {
                // Check special logic for Matrix Working Completed
                if (key === 'Matrix Working Completed' && row['Matrix Working Expected completion Date']) {
                  const expectedDate = new Date(row['Matrix Working Expected completion Date']);
                  const today = new Date();
                  expectedDate.setHours(0, 0, 0, 0);
                  today.setHours(0, 0, 0, 0);

                  const diffInDays = Math.floor((today - expectedDate) / (1000 * 60 * 60 * 24));

                  if (diffInDays > 0) {
                    stepColor = 'red';
                    displayValue = `Overdue - Not Completed (${diffInDays} day${diffInDays > 1 ? 's' : ''} overdue)`;
                  } else {
                    stepColor = 'black';
                    displayValue = 'Pending - Within Expected Time';
                  }
                } else {
                  stepColor = 'red';
                  displayValue = 'Pending - Not Completed';
                }
                firstEmptyFound = true;
              } else {
                stepColor = 'black';
                displayValue = 'Pending - Future Step';
              }
            }

            return (
              <Step key={key}>
                <StepLabel
                  optional={
                    <Typography variant="caption" sx={{ color: stepColor }}>
                      {React.isValidElement(displayValue) ? displayValue : displayValue}
                    </Typography>
                  }
                  sx={{
                    '.MuiStepLabel-label': {
                      color: stepColor,
                      fontWeight: 'bold',
                    },
                  }}
                >
                  {key}
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Box>
    );
  };

  // return (
  //   <>
  //     {types === 'status' && (
  //       <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
  //         <Autocomplete
  //           options={typeOptions}
  //           value={selectedType}
  //           onChange={(event, newValue) => {
  //             setSelectedType(newValue || '');
  //             setSelectedProgram('');
  //           }}
  //           renderInput={(params) => <TextField {...params} label="Filter by Type" variant="outlined" />}
  //           clearOnEscape
  //           sx={{ width: 300 }}
  //         />
  //         <Autocomplete
  //           options={programNames}
  //           value={selectedProgram}
  //           onChange={(event, newValue) => setSelectedProgram(newValue || '')}
  //           renderInput={(params) => <TextField {...params} label="Filter by Program Name" variant="outlined" />}
  //           clearOnEscape
  //           sx={{ width: 300 }}
  //           disabled={!selectedType}
  //         />
  //         <Autocomplete
  //           options={channelOptions}
  //           value={selectedChannel}
  //           onChange={(event, newValue) => setSelectedChannel(newValue || '')}
  //           renderInput={(params) => <TextField {...params} label="Filter by Channel" variant="outlined" />}
  //           clearOnEscape
  //           sx={{ width: 300 }}
  //         />
  //         <Autocomplete
  //           options={statusOptions}
  //           value={selectedStatus}
  //           onChange={(event, newValue) => setSelectedStatus(newValue || '')}
  //           renderInput={(params) => <TextField {...params} label="Filter by Status" variant="outlined" />}
  //           clearOnEscape
  //           sx={{ width: 300 }}
  //         />
  //       </Box>
  //     )}

  //     <Box sx={{ display: 'flex', gap: 4 }}>
  //       <Box sx={getBoxStyles()}>
  //         <DataGrid {...gridProps} />
  //       </Box>

  //       {/* Show stepper details next to table when row is clicked */}
  //       {selectedRowForStepper && (
  //         <Box sx={{
  //           width: '50%',
  //           maxHeight: types === 'status' ? 600 : 500,
  //           overflowY: 'auto',
  //           border: '1px solid #e0e0e0',
  //           borderRadius: 1,
  //           p: 2,
  //           bgcolor: '#fafafa'
  //         }}>
  //           <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
  //             <Typography variant="h6">
  //               📊 Status Details: {selectedRowForStepper?.['Program Name']}
  //             </Typography>
  //             <Button
  //               variant="outlined"
  //               size="small"
  //               onClick={() => setSelectedRowForStepper(null)}
  //             >
  //               Close
  //             </Button>
  //           </Box>
  //           {renderStepper(selectedRowForStepper)}
  //         </Box>
  //       )}

  //     </Box>

  //     {/* Edit Modal */}
  //     <Modal open={openEditModal} onClose={() => setOpenEditModal(false)}>
  //       <Box sx={{
  //         position: 'absolute',
  //         top: '50%',
  //         left: '50%',
  //         transform: 'translate(-50%, -50%)',
  //         width: 600,
  //         maxHeight: '80vh',
  //         overflowY: 'auto',
  //         bgcolor: 'background.paper',
  //         boxShadow: 24,
  //         p: 4,
  //         borderRadius: 2
  //       }}>
  //         <Typography variant="h6" sx={{ mb: 3 }}>Edit Row</Typography>

  //         <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
  //           {editingRow && Object.entries(editingRow).map(([key, value]) => {
  //             if (excludedFields.includes(key) || key === 'Location' || (types === 'status' && key === 'Grand Total')) return null;

  //             const isGrandTotal = key === 'Grand Total';
  //             const isNumeric = typeof value === 'number' || (!isNaN(value) && value !== '');

  //             const handleChange = (e) => {
  //               const newValue = isNumeric ? parseFloat(e.target.value) || 0 : e.target.value;
  //               const updated = { ...editingRow, [key]: newValue };

  //               let total = 0;
  //               for (const [k, v] of Object.entries(updated)) {
  //                 if (!['Grand Total', 'Location'].includes(k) && typeof v === 'number') {
  //                   total += v;
  //                 }
  //               }
  //               updated['Grand Total'] = total;

  //               setEditingRow(updated);
  //             };

  //             return (
  //               <TextField
  //                 key={key}
  //                 label={key}
  //                 fullWidth
  //                 type={isNumeric ? 'number' : 'text'}
  //                 value={value}
  //                 onChange={isGrandTotal ? undefined : handleChange}
  //                 InputProps={{ readOnly: isGrandTotal }}
  //               />
  //             );
  //           })}
  //         </Box>

  //         <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
  //           <Button variant="outlined" onClick={() => setOpenEditModal(false)}>Cancel</Button>
  //           <Button variant="contained" onClick={handleSaveEdit}>Save</Button>
  //         </Box>
  //       </Box>
  //     </Modal>
  //   </>
  // );

 return (
    <>
      {types === 'status' && (
        <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
          <Autocomplete
            options={typeOptions}
            value={selectedType}
            onChange={(event, newValue) => {
              setSelectedType(newValue || '');
              setSelectedProgram('');
            }}
            renderInput={(params) => <TextField {...params} label="Filter by Type" variant="outlined" />}
            clearOnEscape
            sx={{ width: 300 }}
          />
          <Autocomplete
            options={programNames}
            value={selectedProgram}
            onChange={(event, newValue) => setSelectedProgram(newValue || '')}
            renderInput={(params) => <TextField {...params} label="Filter by Program Name" variant="outlined" />}
            clearOnEscape
            sx={{ width: 300 }}
            disabled={!selectedType}
          />
          <Autocomplete
            options={channelOptions}
            value={selectedChannel}
            onChange={(event, newValue) => setSelectedChannel(newValue || '')}
            renderInput={(params) => <TextField {...params} label="Filter by Channel" variant="outlined" />}
            clearOnEscape
            sx={{ width: 300 }}
          />
          <Autocomplete
            options={statusOptions}
            value={selectedStatus}
            onChange={(event, newValue) => setSelectedStatus(newValue || '')}
            renderInput={(params) => <TextField {...params} label="Filter by Status" variant="outlined" />}
            clearOnEscape
            sx={{ width: 300 }}
          />
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 4 }}>
        <Box sx={getBoxStyles()}>
          <DataGrid {...gridProps} />
        </Box>

        {/* PDF Viewer Section - Fixed */}
        {types === 'overall' && pdfUrl != '' && (
          <Box sx={{
            width: '40%',
            minHeight: 500,
            maxHeight: types === 'status' ? 600 : 500,
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            bgcolor: '#fafafa',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* PDF Header with Download Button */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              borderBottom: '1px solid #e0e0e0',
              bgcolor: '#f5f5f5'
            }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                📄 PDF Document
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = pdfUrl;
                  link.download = 'document.pdf';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                sx={{ bgcolor: '#1976d2' }}
              >
                Download
              </Button>
            </Box>

            {/* PDF Viewer */}
            <Box sx={{ flex: 1, position: 'relative' }}>
              <iframe
  src={`https://docs.google.com/gview?url=${encodeURIComponent(pdfUrl)}&embedded=true`}
  style={{
    width: '100%',
    height: '100%',
    border: 'none',
    borderRadius: '0 0 4px 4px'
  }}
  title="PDF Viewer"
/>
            </Box>
          </Box>
        )}

        {/* Show stepper details next to table when row is clicked */}
        {selectedRowForStepper && (
          <Box sx={{
            width: '50%',
            maxHeight: types === 'status' ? 600 : 500,
            overflowY: 'auto',
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            p: 2,
            bgcolor: '#fafafa'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                📊 Status Details: {selectedRowForStepper?.['Program Name']}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setSelectedRowForStepper(null)}
              >
                Close
              </Button>
            </Box>
            {renderStepper(selectedRowForStepper)}
          </Box>
        )}
      </Box>

      {/* Edit Modal */}
      <Modal open={openEditModal} onClose={() => setOpenEditModal(false)}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 600,
          maxHeight: '80vh',
          overflowY: 'auto',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2
        }}>
          <Typography variant="h6" sx={{ mb: 3 }}>Edit Row</Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            {editingRow && Object.entries(editingRow).map(([key, value]) => {
              if (excludedFields.includes(key) || key === 'Location' || (types === 'status' && key === 'Grand Total')) return null;

              const isGrandTotal = key === 'Grand Total';
              const isNumeric = typeof value === 'number' || (!isNaN(value) && value !== '');

              const handleChange = (e) => {
                const newValue = isNumeric ? parseFloat(e.target.value) || 0 : e.target.value;
                const updated = { ...editingRow, [key]: newValue };

                let total = 0;
                for (const [k, v] of Object.entries(updated)) {
                  if (!['Grand Total', 'Location'].includes(k) && typeof v === 'number') {
                    total += v;
                  }
                }
                updated['Grand Total'] = total;

                setEditingRow(updated);
              };

              return (
                <TextField
                  key={key}
                  label={key}
                  fullWidth
                  type={isNumeric ? 'number' : 'text'}
                  value={value}
                  onChange={isGrandTotal ? undefined : handleChange}
                  InputProps={{ readOnly: isGrandTotal }}
                />
              );
            })}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <Button variant="outlined" onClick={() => setOpenEditModal(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveEdit}>Save</Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}